
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QuoteMessage } from "./QuoteMessage";
import { MessagesSquare } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const sampleQuotes = [
  {
    content: "Criticism is dangerous because it wounds a person's precious pride, hurts his sense of importance, and arouses resentment.",
    author: "Dale Carnegie"
  },
  {
    content: "If your story stinks, your ink stinks.",
    author: "Chip Gaines"
  },
  {
    content: "Listen more than you talk.",
    author: "Richard Branson"
  },
  {
    content: "Creativity is a wild mind and a disciplined eye.",
    author: "Dorothy Parker"
  },
  {
    content: "The best way to predict the future is to create it.",
    author: "Peter Drucker"
  },
  {
    content: "Success is not final, failure is not fatal: It is the courage to continue that counts.",
    author: "Winston Churchill"
  },
  {
    content: "The only limit to our realization of tomorrow will be our doubts of today.",
    author: "Franklin D. Roosevelt"
  },
  {
    content: "The journey of a thousand miles begins with one step.",
    author: "Lao Tzu"
  },
  {
    content: "It does not matter how slowly you go as long as you do not stop.",
    author: "Confucius"
  },
  {
    content: "Everything you've ever wanted is on the other side of fear.",
    author: "George Addair"
  }
];

export const QuotePreview = () => {
  const [displayQuotes, setDisplayQuotes] = useState<(typeof sampleQuotes)[0][]>([]);
  const [visibleQuotes, setVisibleQuotes] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<string>("");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Shuffle quotes and select 10 random ones
  useEffect(() => {
    const shuffled = [...sampleQuotes].sort(() => 0.5 - Math.random());
    setDisplayQuotes(shuffled.slice(0, 10));
    // Show first quote immediately
    setVisibleQuotes(1);
  }, []);

  // Show additional quotes at intervals
  useEffect(() => {
    if (visibleQuotes >= displayQuotes.length || visibleQuotes === 0) return;
    
    const timer = setTimeout(() => {
      setVisibleQuotes(prev => prev + 1);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [visibleQuotes, displayQuotes.length]);

  // Scroll to bottom when new quote appears
  useEffect(() => {
    if (messagesContainerRef.current && scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [visibleQuotes]);

  // Update time
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      setCurrentTime(`${hours}:${minutes} ${ampm}`);
    };

    updateTime(); // Initial update
    const interval = setInterval(updateTime, 1000 * 60); // Update every minute

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  return (
    <div className="w-full max-w-lg mx-auto">
      <motion.div 
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="relative mx-auto bg-black/90 rounded-[24px] p-4 shadow-2xl border border-white/10 px-[3px] py-[8px]"
      >
        {/* Phone Header */}
        <div className="flex justify-between items-center mb-2 px-4 text-white text-sm">
          <span>{currentTime}</span>
          <div className="flex items-center gap-1">
            <MessagesSquare className="w-4 h-4" />
            <span>4G</span>
          </div>
        </div>

        {/* Messages Container */}
        <div className="bg-gray-100 rounded-[18px] p-6 h-[400px]">
          <ScrollArea ref={scrollAreaRef} className="h-full pr-4">
            <div ref={messagesContainerRef} className="space-y-4">
              <AnimatePresence>
                {displayQuotes.slice(0, visibleQuotes).map((quote, index) => (
                  <QuoteMessage key={index} content={quote.content} author={quote.author} />
                ))}
              </AnimatePresence>
            </div>
          </ScrollArea>
        </div>

        {/* Phone Bottom Line */}
        <div className="mt-4 flex justify-center">
          <div className="w-1/3 h-1 bg-white/20 rounded-full"></div>
        </div>
      </motion.div>
    </div>
  );
};
